import { useContext } from 'react';

import { TimestampCalendarPicker } from '../../components/v2/calendar/timestamp-calendar-picker';
import InlineOptions from '../../components/v2/inline-options';
import TokenSelector from '../../components/v2/token-selector';
import {
  MarketChangeContext,
  MarketChangeProvider,
  MarketChangeWindowPreset,
} from '../../providers/market-change/market-change-provider';
import { MarketChangeSurfaceProvider } from '../../providers/market-change/market-change-surface-provider';
import { MarketChangeChainProvider } from '../../providers/market-change/market-change-chain-provider';
import OptionsChainPanel from './options-chain-panel';
import VolSurfaceChangePanel from './vol-surface-change-panel';

import './styles.scss';

// IM-2597 increment 1 - scaffold only. The shared selection (market, T1, T2)
// lives in MarketChangeProvider; the control strip binds to it and the panel
// grid below is placeholders. No data, no WebSocket - later increments fill the
// panels (surface change, options chain, spot/futures, term structure, skew,
// realized vs implied, open interest) from this one selection.

// Quick-set windows. '00:00 UTC' is since midnight UTC (timezone-explicit so it
// is unambiguous across regions); 24h/7d/30d are rolling lookbacks. Editing a
// datetime directly drops the provider's preset to 'custom', which matches none
// of these, so no tile stays highlighted - that is the custom state.
const PRESET_OPTIONS: { value: MarketChangeWindowPreset; label: string }[] = [
  { value: 'midnight', label: '00:00 UTC' },
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
];

// Eventual panels, named so the scaffold reads as the finished layout. Each is a
// bordered placeholder card until its increment lands (IM-2598 vol-surface-
// change, IM-2599 options-chain); the remainder are still placeholders.
const PANELS: { key: string; title: string }[] = [
  { key: 'vol-surface-change', title: 'Vol Surface Change' },
  { key: 'options-chain', title: 'Options Chain' },
  { key: 'spot-futures', title: 'Spot & Futures' },
  { key: 'term-structure-change', title: 'Term Structure Change' },
  { key: 'skew-rr-bf', title: 'Skew RR/BF' },
  { key: 'realized-vs-implied', title: 'Realized vs Implied' },
  { key: 'open-interest', title: 'Open Interest' },
];

const ControlStrip = () => {
  const { market, setMarket, t1, setT1, t2, setT2, t2IsLive, goLive, preset, applyPreset } =
    useContext(MarketChangeContext);

  return (
    <section className="market-change__controls" aria-label="Market change selection">
      <div className="market-change__control">
        <TokenSelector token={market} setToken={setMarket} fromRegistry />
      </div>

      <div className="market-change__window">
        <span className="market-change__label" id="market-change-window-label">
          Window
        </span>
        <div
          className="market-change__window-row"
          role="group"
          aria-labelledby="market-change-window-label"
        >
          <TimestampCalendarPicker
            className="market-change__t1-picker"
            handleChangeDatetime={setT1}
            initialTimestamp={t1}
            displayTimestamp={t1}
            isLive={false}
            inputIsUTC
            timezoneLabel="UTC"
            liveLabels={{ historic: 'T1' }}
            reset={() => undefined}
          />
          <TimestampCalendarPicker
            handleChangeDatetime={setT2}
            initialTimestamp={t2}
            displayTimestamp={t2}
            isLive={t2IsLive}
            inputIsUTC
            timezoneLabel="UTC"
            liveLabels={{ onLive: 'Live', historic: 'T2' }}
            reset={goLive}
            resetLabel="Live"
          />
          <InlineOptions
            options={PRESET_OPTIONS}
            selectedOption={preset}
            onOptionChange={(value) => applyPreset(value as MarketChangeWindowPreset)}
          />
        </div>
      </div>
    </section>
  );
};

// Panels with a built body, keyed the same as PANELS, so the grid below can
// filter them out of the "coming soon" fallback without re-listing the keys.
const BUILT_PANEL_KEYS = ['vol-surface-change', 'options-chain'];

const PanelGrid = () => (
  <div className="market-change__grid">
    <VolSurfaceChangePanel />
    <OptionsChainPanel />
    {PANELS.filter((panel) => !BUILT_PANEL_KEYS.includes(panel.key)).map((panel) => (
      <section key={panel.key} className="market-change__panel">
        <h3 className="market-change__panel-title">{panel.title}</h3>
        <p className="market-change__panel-note">panel coming soon</p>
      </section>
    ))}
  </div>
);

const MarketChange = () => (
  <MarketChangeProvider>
    <MarketChangeSurfaceProvider>
      <MarketChangeChainProvider>
        <div className="market-change">
          <header className="market-change__header">
            <h2 className="market-change__heading">Market Change</h2>
          </header>
          <ControlStrip />
          <PanelGrid />
        </div>
      </MarketChangeChainProvider>
    </MarketChangeSurfaceProvider>
  </MarketChangeProvider>
);

export default MarketChange;
